import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import AdminPageLayout from "../../../../components/FullPageLayout/AdminPageLayout";
import { Tabs, Tab } from "@mui/material";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import { Route, Redirect } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  adminPolicies,
  policyDetails,
  policyDetailsVersions,
  policyDetailsAssignments,
  policyDetailsExclusions,
  policyDetailsSettings,
  buildPolicyDetailsSettingsRoute,
  buildPolicyDetailsVersionsRoute,
  buildPolicyDetailsAssignmentsRoute,
  buildPolicyDetailsExclusionsRoute
} from "../../../../routes/AdminRoutes";
import {
  CourseAssignments,
  CourseExclusions
} from "../../Courses/CourseDetails";
import PolicySettings from "./PolicySettings";
import PolicyVersions from "./PolicyVersions";
import LoadingSpinner from "../../../../components/LoadingSpinner/LoadingSpinner";
import { contentOwners } from "../../../../constants";
import Breadcrumb from "../../../../components/Breadcrumb/Breadcrumb";
import usePolicies from "../../../../hooks/policies";

const getPolicyDetailsTabs = (policyId, сustomizableContent) => {
  let routes = [
    {
      route: buildPolicyDetailsSettingsRoute(policyId),
      component: PolicySettings,
      title: "managePolicies.details.tabs.settings"
    },
    {
      route: buildPolicyDetailsVersionsRoute(policyId),
      component: PolicyVersions,
      title: "managePolicies.details.tabs.versions"
    },
    {
      route: buildPolicyDetailsAssignmentsRoute(policyId),
      component: CourseAssignments,
      title: "managePolicies.details.tabs.assignments"
    },
    {
      route: buildPolicyDetailsExclusionsRoute(policyId),
      component: CourseExclusions,
      title: "managePolicies.details.tabs.exclusions"
    }
  ];

  return routes;
};

const PolicyDetails = ({
  policyId,
  policyData,
  onGoBackClick,
  onTabChange,
  pathname
}) => {
  const { t } = useTranslation();
  const [tabs, setTabs] = React.useState([]);
  const [currentTab, setCurrentTab] = React.useState(0);

  useEffect(() => {
    if (policyData) {
      setTabs(getPolicyDetailsTabs(policyId));
    }
  }, [policyData]);

  useEffect(() => {
    const currentPathIndex = tabs.findIndex(tab => tab.route === pathname);
    if (currentPathIndex !== -1) {
      setCurrentTab(currentPathIndex);
    }
  }, [pathname, tabs]);

  const handleTabChange = newTab => {
    setCurrentTab(newTab);
    onTabChange(tabs[newTab]);
  };

  const сustomizableContent = () => {
    if (policyData) {
      const { contentOwner } = policyData;
      const isCustomizable =
        contentOwner === contentOwners.custom ||
        contentOwner === contentOwners.master;
      return isCustomizable;
    }
  };

  return (
    <AdminPageLayout
      title={
        <Breadcrumb routes={["Policies", policyData ? policyData.name : ""]} />
      }
      onGoBackClick={onGoBackClick}
    >
      {policyData ? (
        <>
          <Tabs
            value={currentTab}
            onChange={(e, newTab) => handleTabChange(newTab)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons={false}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                label={t(tab.title)}
                data-qa="policy-details-tab"
              />
            ))}
          </Tabs>
          <>
            <Route
              exact
              path={policyDetails}
              render={() => (
                <Redirect to={buildPolicyDetailsSettingsRoute(policyId)} />
              )}
            />
            <Route
              exact
              path={policyDetailsSettings}
              render={props => (
                <PolicySettings course={policyData} {...props} />
              )}
            />
            <Route
              exact
              path={policyDetailsAssignments}
              component={CourseAssignments}
            />
            <Route
              exact
              path={policyDetailsExclusions}
              render={props => (
                <CourseExclusions courseId={policyData.id} {...props} />
              )}
            />
            <Route
              exact
              path={policyDetailsVersions}
              render={props => (
                <PolicyVersions
                  parentId={policyData.id}
                  сustomizableContent={сustomizableContent()}
                  {...props}
                />
              )}
            />
          </>
        </>
      ) : (
        <LoadingSpinner />
      )}
    </AdminPageLayout>
  );
};

const PolicyDetailsContainer = ({
  match: {
    params: { policyId }
  },
  location: { pathname }
}) => {
  const dispatch = useDispatch();

  const [policyData, setPolicyData] = useState(null);
  const { fetchPolicy } = usePolicies();

  useEffect(() => {
    const loadPolicy = async () => {
      const data = await fetchPolicy(policyId);
      setPolicyData(data);
    };
    loadPolicy();
  }, [policyId]);

  function handleTabChange(newTab) {
    dispatch(push(newTab.route));
  }

  const handleGoBack = () => {
    dispatch(push(adminPolicies));
  };

  return (
    <PolicyDetails
      policyId={policyId}
      onGoBackClick={handleGoBack}
      policyData={policyData}
      onTabChange={handleTabChange}
      pathname={pathname}
    />
  );
};

PolicyDetailsContainer.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      /**
       * The course ID for the course
       */
      policyId: PropTypes.string
    }),
    path: PropTypes.string,
    url: PropTypes.string
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string
  })
};

export default PolicyDetailsContainer;
